/**
 * What a paper is waiting for (design v2 screen 4, the New paper panel before it can be used):
 * a textbook with flashcards and a confirmed exam format, each a row in plain words with its
 * state and the action that moves it on. Pure, so the screen holds none of the branches.
 */
import type { Doc, Profile } from "../api/api";
import type { PillKind } from "../upload/rows";

export type RowKind = "done" | "progress" | "action" | "problem" | "todo";

export type ReadinessRow = {
  name: "Textbook" | "Exam format";
  text: string;
  kind: RowKind;
  /** The handler an action row's button calls. */
  action?: { to: "review"; bookId: string } | { to: "format" };
};

export type Readiness = { ready: boolean; rows: [ReadinessRow, ReadinessRow] };

/** The existing pills, one per kind; no new colour. */
export const PILL: Record<RowKind, { kind: PillKind; label: string }> = {
  done: { kind: "ready", label: "Done" },
  progress: { kind: "building", label: "In progress" },
  action: { kind: "check", label: "Your turn" },
  problem: { kind: "failed", label: "Needs attention" },
  todo: { kind: "plain", label: "Not yet" },
};

// The most advanced book describes the row: ready > confirmed/ingesting > review > detecting > failed.
const RANK: Record<string, number> = {
  ready: 5,
  confirmed: 4,
  ingesting: 4,
  review: 3,
  detecting: 2,
  none: 2,
  failed: 1,
};

function bookRow(docs: Doc[], madeChapters: number | null): ReadinessRow {
  const books = docs.filter(
    (d) => d.kind === "textbook" && d.status !== "rejected" && d.status !== "withdrawn",
  );
  const best = books.reduce<Doc | null>(
    (a, d) => (!a || RANK[d.book_status ?? "none"]! > RANK[a.book_status ?? "none"]! ? d : a),
    null,
  );
  const name = "Textbook" as const;
  if (!best) return { name, text: "Add your textbook", kind: "todo" };
  switch (best.book_status) {
    case null:
    case "detecting":
      return { name, text: "Finding the chapters…", kind: "progress" };
    case "review":
      return {
        name,
        text: "Check the chapters",
        kind: "action",
        action: { to: "review", bookId: best.book_id ?? "" },
      };
    case "confirmed":
    case "ingesting":
      return {
        name,
        text: "Making your flashcards… papers open when they're ready",
        kind: "progress",
      };
    case "failed":
      return {
        name,
        text: "Your flashcards didn't finish. Try again from the book above",
        kind: "problem",
      };
    case "ready":
      // A paper is written from chapters with flashcards; a ready book may have none left.
      return madeChapters === 0
        ? {
            name,
            text: "No chapter has flashcards yet. Add them from The rest of the book",
            kind: "todo",
          }
        : { name, text: "Flashcards ready", kind: "done" };
  }
}

function formatRow(profile: Profile | null, learning: boolean): ReadinessRow {
  const name = "Exam format" as const;
  if (profile === null)
    return learning
      ? { name, text: "Learning the format…", kind: "progress" }
      : { name, text: "Add a past paper so we learn the exam format", kind: "todo" };
  switch (profile.status) {
    case "draft":
      return { name, text: "Check the format", kind: "action", action: { to: "format" } };
    case "failed":
      return { name, text: "We couldn't read the format. Try another past paper", kind: "problem" };
    case "confirmed":
      return { name, text: "Format confirmed", kind: "done" };
  }
}

/**
 * `madeChapters` is the ready book's chapters with flashcards, or null until they are read.
 */
export function paperReadiness(
  docs: Doc[],
  profile: Profile | null,
  learning: boolean,
  madeChapters: number | null = null,
): Readiness {
  const rows: [ReadinessRow, ReadinessRow] = [
    bookRow(docs, madeChapters),
    formatRow(profile, learning),
  ];
  return { ready: rows.every((r) => r.kind === "done"), rows };
}
